import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import * as Dialog from '@radix-ui/react-dialog'
import { ChevronLeft } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { SettingsMainGrid } from '@/components/settings/SettingsParts'
import { Container, ContainerContent } from '@/components/ui/Container'
import { toast } from '@/hooks/use-toast'
import {
  applyAutomationToSettings,
  applyDateBehaviorToSettings,
  applyErpSyncToSettings,
  applyLeadTimeToSettings,
  applyLineFlagsToSettings,
  applyParseFormatToSettings,
  applyThresholdsAndFlagsToSettings,
  automationFromSettings,
  dateBehaviorFromSettings,
  erpSyncFromSettings,
  flaggedFieldsFromSettings,
  flaggedLineFieldsFromSettings,
  leadTimeFromSettings,
  parseFormatFromSettings,
  settingsEqual,
  thresholdsFromSettings,
  toUpdateRequest,
  type DateBehaviorSettings,
  type DeviationThresholds,
  type ParseFormatSettings,
} from '@/features/order-confirmation/settings/orderConfirmationSettingsMappers'
import {
  AdvancedSettingsSection,
  DateBehaviorSection,
  DeviationThresholdsSection,
  ErpSyncSection,
  FlagDifferencesSection,
  FullyAutomaticValidationSection,
  LeadTimeSection,
  ParseFormatSection,
} from '@/features/order-confirmation/settings/OrderConfirmationSettingsSections'
import {
  OC_HEAD_COMPARISON_FIELDS,
  type OcHeadComparisonFieldId,
} from '@/features/order-confirmation/settings/ocHeadComparisonFields'
import { OC_LINE_COMPARISON_FIELDS } from '@/features/order-confirmation/settings/ocLineComparisonFields'
import type { OcLineComparisonFlagFieldId } from '@/types/orderConfirmationSettings'
import {
  supplierSettingsQueryKey,
  useSupplierSettings,
} from '@/features/order-confirmation/supplierSettingsQueries'
import { useWorkflowSuppliers } from '@/features/order-confirmation/workflowSuppliersQueries'
import { useWorkflowOrderConfirmationSettings } from '@/features/order-confirmation/workflowOrderConfirmationSettingsQueries'
import {
  deleteSupplierSettings,
  saveSupplierSettings,
} from '@/services/api'
import type { OrderConfirmationSettings } from '@/types/orderConfirmationSettings'

export function SupplierSettingsPage() {
  const { workflowId, supplierId } = useParams<{ workflowId: string; supplierId: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const isCreateMode = searchParams.get('create') === '1'

  const [savedSettings, setSavedSettings] = useState<OrderConfirmationSettings | null>(null)
  const [draftSettings, setDraftSettings] = useState<OrderConfirmationSettings | null>(null)
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false)

  const { data: suppliersData } = useWorkflowSuppliers(workflowId)
  const supplier = suppliersData?.suppliers.find((item) => item.id === supplierId)

  const {
    data: supplierSettings,
    isLoading: supplierSettingsLoading,
    isError: supplierSettingsError,
    error: supplierSettingsLoadError,
  } = useSupplierSettings(workflowId, supplierId, !isCreateMode)

  const {
    data: globalSettings,
    isLoading: globalSettingsLoading,
    isError: globalSettingsError,
    error: globalSettingsLoadError,
  } = useWorkflowOrderConfirmationSettings(workflowId)

  useEffect(() => {
    if (isCreateMode) {
      if (!globalSettings) return
      const draft = { ...globalSettings, id: '' }
      setSavedSettings(null)
      setDraftSettings(draft)
      return
    }

    if (supplierSettingsLoading) return

    if (supplierSettings) {
      setSavedSettings(supplierSettings)
      setDraftSettings(supplierSettings)
      return
    }

    if (!supplierSettingsLoading && !supplierSettings && globalSettings) {
      navigate(`/workflow/${workflowId}/suppliers/${supplierId}/settings?create=1`, { replace: true })
    }
  }, [
    globalSettings,
    isCreateMode,
    navigate,
    supplierId,
    supplierSettings,
    supplierSettingsLoading,
    workflowId,
  ])

  const draftThresholds = draftSettings ? thresholdsFromSettings(draftSettings) : null
  const draftDateBehavior = draftSettings ? dateBehaviorFromSettings(draftSettings) : null
  const draftParseFormat = draftSettings ? parseFormatFromSettings(draftSettings) : null
  const draftLeadTime = draftSettings ? leadTimeFromSettings(draftSettings) : null
  const draftAutomation = draftSettings ? automationFromSettings(draftSettings) : null
  const draftErpSync = draftSettings ? erpSyncFromSettings(draftSettings) : null
  const draftFlaggedFields = draftSettings ? flaggedFieldsFromSettings(draftSettings) : null
  const draftFlaggedLineFields = draftSettings ? flaggedLineFieldsFromSettings(draftSettings) : null

  const hasUnsavedChanges =
    isCreateMode
      ? !!draftSettings
      : savedSettings && draftSettings
        ? !settingsEqual(savedSettings, draftSettings)
        : false

  const saveMutation = useMutation({
    mutationFn: () => {
      if (!workflowId || !supplierId || !draftSettings) {
        throw new Error('Settings are not loaded')
      }
      return saveSupplierSettings(workflowId, supplierId, toUpdateRequest(draftSettings))
    },
    onSuccess: (updated) => {
      setSavedSettings(updated)
      setDraftSettings(updated)
      queryClient.setQueryData(supplierSettingsQueryKey(workflowId!, supplierId!), updated)
      void queryClient.invalidateQueries({ queryKey: ['workflow-suppliers', workflowId] })
      void queryClient.invalidateQueries({ queryKey: ['purchase-order-validation', workflowId] })
      toast({ title: isCreateMode ? 'Supplier settings created' : 'Supplier settings saved' })
      if (isCreateMode) {
        navigate(`/workflow/${workflowId}/suppliers/${supplierId}/settings`, { replace: true })
      }
    },
    onError: (err: Error) => {
      toast({
        title: 'Could not save supplier settings',
        description: err.message,
        variant: 'destructive',
      })
    },
  })

  const removeMutation = useMutation({
    mutationFn: () => {
      if (!workflowId || !supplierId) {
        throw new Error('Supplier is not loaded')
      }
      return deleteSupplierSettings(workflowId, supplierId)
    },
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: supplierSettingsQueryKey(workflowId!, supplierId!) })
      void queryClient.invalidateQueries({ queryKey: ['workflow-suppliers', workflowId] })
      void queryClient.invalidateQueries({ queryKey: ['purchase-order-validation', workflowId] })
      toast({ title: 'Supplier settings removed' })
      navigate(`/workflow/${workflowId}/suppliers`)
    },
    onError: (err: Error) => {
      toast({
        title: 'Could not remove supplier settings',
        description: err.message,
        variant: 'destructive',
      })
    },
  })

  function updateDraftFromThresholds(thresholds: DeviationThresholds) {
    setDraftSettings((prev) => {
      if (!prev) return prev
      return applyThresholdsAndFlagsToSettings(
        prev,
        thresholds,
        flaggedFieldsFromSettings(prev),
      )
    })
  }

  function toggleDateBehavior(key: keyof DateBehaviorSettings, checked: boolean) {
    setDraftSettings((prev) => {
      if (!prev) return prev
      const dateBehavior = dateBehaviorFromSettings(prev)
      dateBehavior[key] = checked
      return applyDateBehaviorToSettings(prev, dateBehavior)
    })
  }

  function toggleParseFormat(key: keyof ParseFormatSettings, checked: boolean) {
    setDraftSettings((prev) => {
      if (!prev) return prev
      const parseFormat = parseFormatFromSettings(prev)
      parseFormat[key] = checked
      return applyParseFormatToSettings(prev, parseFormat)
    })
  }

  function toggleUseLeadTime(checked: boolean) {
    setDraftSettings((prev) => {
      if (!prev) return prev
      return applyLeadTimeToSettings(prev, {
        ...leadTimeFromSettings(prev),
        useLeadTimeWhenCalculatingArrival: checked,
      })
    })
  }

  function updateLeadTimeInDays(days: number) {
    setDraftSettings((prev) => {
      if (!prev) return prev
      return applyLeadTimeToSettings(prev, {
        ...leadTimeFromSettings(prev),
        leadTimeInDays: days,
      })
    })
  }

  function toggleAutomation(checked: boolean) {
    setDraftSettings((prev) => {
      if (!prev) return prev
      return applyAutomationToSettings(prev, { fullyAutomaticValidation: checked })
    })
  }

  function toggleErpSync(checked: boolean) {
    setDraftSettings((prev) => {
      if (!prev) return prev
      return applyErpSyncToSettings(prev, { updateSupplierOrderNumberOnErpSync: checked })
    })
  }

  function toggleFlaggedLineField(id: OcLineComparisonFlagFieldId, checked: boolean) {
    setDraftSettings((prev) => {
      if (!prev) return prev
      const flagged = flaggedLineFieldsFromSettings(prev)
      if (checked) flagged.add(id)
      else flagged.delete(id)
      return applyLineFlagsToSettings(prev, flagged)
    })
  }

  function toggleAllFlaggedLineFields(checked: boolean) {
    setDraftSettings((prev) => {
      if (!prev) return prev
      const flagged = checked
        ? new Set(OC_LINE_COMPARISON_FIELDS.map((field) => field.id))
        : new Set<OcLineComparisonFlagFieldId>()
      return applyLineFlagsToSettings(prev, flagged)
    })
  }

  function toggleFlaggedField(id: OcHeadComparisonFieldId, checked: boolean) {
    setDraftSettings((prev) => {
      if (!prev) return prev
      const flagged = flaggedFieldsFromSettings(prev)
      if (checked) flagged.add(id)
      else flagged.delete(id)
      return applyThresholdsAndFlagsToSettings(prev, thresholdsFromSettings(prev), flagged)
    })
  }

  function toggleAllFlaggedFields(checked: boolean) {
    setDraftSettings((prev) => {
      if (!prev) return prev
      const flagged = checked
        ? new Set(OC_HEAD_COMPARISON_FIELDS.map((field) => field.id))
        : new Set<OcHeadComparisonFieldId>()
      return applyThresholdsAndFlagsToSettings(prev, thresholdsFromSettings(prev), flagged)
    })
  }

  if (!workflowId || !supplierId) {
    return (
      <div className="flex items-center justify-center py-16 text-sm text-gray-500">
        No supplier selected.
      </div>
    )
  }

  const isLoading = isCreateMode
    ? globalSettingsLoading
    : supplierSettingsLoading || globalSettingsLoading

  if (isLoading || !draftSettings || !draftThresholds || !draftDateBehavior || !draftParseFormat || !draftLeadTime || !draftAutomation || !draftErpSync || !draftFlaggedFields || !draftFlaggedLineFields) {
    return (
      <div className="flex items-center justify-center py-16 text-sm text-gray-400">Loading…</div>
    )
  }

  if (supplierSettingsError || globalSettingsError) {
    const message =
      (supplierSettingsLoadError instanceof Error && supplierSettingsLoadError.message) ||
      (globalSettingsLoadError instanceof Error && globalSettingsLoadError.message) ||
      'Could not load supplier settings'
    return (
      <div className="flex items-center justify-center py-16 text-sm text-red-600">{message}</div>
    )
  }

  const supplierName = supplier?.name ?? 'Supplier'

  return (
    <div className="container mx-auto">
      <Container>
        <ContainerContent className="space-y-8 py-6">
          <header className="space-y-4">
            <Link
              to={`/workflow/${workflowId}/suppliers`}
              className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-900"
            >
              <ChevronLeft className="h-4 w-4" />
              Back to suppliers
            </Link>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">{supplierName}</h1>
                <p className="mt-2 text-sm text-gray-600">
                  {isCreateMode
                    ? 'Create custom settings for this supplier. They will override the global settings.'
                    : 'Custom settings for this supplier override the global settings.'}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {!isCreateMode && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowRemoveConfirm(true)}
                  >
                    Remove supplier settings
                  </Button>
                )}
                <Button variant="cta"
                  type="button"
                  disabled={!hasUnsavedChanges || saveMutation.isPending}
                  onClick={() => saveMutation.mutate()}
                >
                  {saveMutation.isPending ? 'Saving…' : isCreateMode ? 'Create' : 'Save'}
                </Button>
              </div>
            </div>
          </header>

          <FullyAutomaticValidationSection
            automation={draftAutomation}
            onToggle={toggleAutomation}
          />
          <SettingsMainGrid
            wide={
              <FlagDifferencesSection
                flaggedFields={draftFlaggedFields}
                onToggle={toggleFlaggedField}
                onToggleAll={toggleAllFlaggedFields}
                flaggedLineFields={draftFlaggedLineFields}
                onToggleLine={toggleFlaggedLineField}
                onToggleAllLine={toggleAllFlaggedLineFields}
              />
            }
            narrow={
              <>
                <DeviationThresholdsSection
                  thresholds={draftThresholds}
                  onDraftChange={updateDraftFromThresholds}
                />
                <ErpSyncSection erpSync={draftErpSync} onToggle={toggleErpSync} />
              </>
            }
          />
          <AdvancedSettingsSection>
            {/* Dates first: lead time and date behaviour both decide the arrival date compared. */}
            <div className="space-y-6">
              <LeadTimeSection
                leadTime={draftLeadTime}
                onToggleUseLeadTime={toggleUseLeadTime}
                onLeadTimeInDaysChange={updateLeadTimeInDays}
              />
              <DateBehaviorSection dateBehavior={draftDateBehavior} onToggle={toggleDateBehavior} />
            </div>
            <ParseFormatSection parseFormat={draftParseFormat} onToggle={toggleParseFormat} />
          </AdvancedSettingsSection>
        </ContainerContent>
      </Container>

      <Dialog.Root open={showRemoveConfirm} onOpenChange={setShowRemoveConfirm}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-40 bg-black/25 backdrop-blur-[1px]" />
          <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-sm -translate-x-1/2 -translate-y-1/2 rounded-panel bg-white p-6 shadow-lg">
            <Dialog.Title className="text-base font-semibold text-gray-900">
              Remove supplier settings
            </Dialog.Title>
            <Dialog.Description className="mt-2 text-sm text-gray-500">
              Remove custom settings for {supplierName}? This supplier will use the global settings
              instead.
            </Dialog.Description>
            <div className="mt-6 flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowRemoveConfirm(false)}>
                Cancel
              </Button>
              <Button
                type="button"
                variant="destructive"
                disabled={removeMutation.isPending}
                onClick={() => removeMutation.mutate()}
              >
                {removeMutation.isPending ? 'Removing…' : 'Remove settings'}
              </Button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  )
}
